import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import firebase, { functions } from '@/plugins/firebase';
import { AUTH_REQUEST_STATES } from "@/constants/AuthRequestStates";
var AuthCredential = firebase.auth.AuthCredential;
import { VALIDATION_RULES } from "@/constants/ValidationRules";
import VInput from "@/classes/VInput";
import { Watch } from "vue-property-decorator";
import { TRANSACTION_CATEGORIES } from "@/constants/TransactionCategories";
import moment from 'moment';
import UserStatistics from "@/components/UserStatistics.vue";
import { getInstructorGradeForDate } from '@/utils/GradeResolver';
import Snackbar from "@/components/Snackbar.vue";
import ContractSignDialog from "@/components/ContractSignDialog.vue";
import DocumentSignDialog from "@/components/DocumentSignDialog.vue";
import { CONTRACT_STATUS_TITLES } from "@/constants/ContractTypes";
import { DOCUMENT_STATUS_TITLES } from "@/constants/DocumentTypes";
const PENDING_SORT_DATE = '9999';
let UserMenu = class UserMenu extends Vue {
    listeners = [];
    authRequestStates = AUTH_REQUEST_STATES;
    transactionHeaders = [
        { text: 'Datum a čas', value: 'data.dateTime' },
        { text: 'Kategorie', value: 'data.category' },
        { text: 'Popis', value: 'data.description' },
        { text: 'Částka', value: 'data.value' },
    ];
    get userTransactions() {
        return this.$store.getters['transaction/byUser'](this.currentUserId);
    }
    printTransactionHeader(header, item) {
        if (header.value === 'data.dateTime') {
            return moment(item.data.dateTime).toDate().toLocaleString();
        }
        if (header.value === 'data.category') {
            return TRANSACTION_CATEGORIES.find(cat => cat.id === item.data.category)?.title ?? '';
        }
        if (header.value === 'data.value') {
            let value = item.data.value;
            if (item.data.from === this.currentUserId) {
                value *= -1;
            }
            return `${value.toLocaleString()},- Kč`;
        }
        return item.data.description;
    }
    get instructors() {
        return this.$store.getters['instructor/all'];
    }
    transactionDialog = false;
    statisticsDialog = false;
    wageDialog = false;
    documentsDialog = false;
    paperHeaders = [
        { text: 'Název', value: 'title' },
        { text: 'Typ', value: 'key' },
        { text: 'Stav', value: 'status' },
        { text: 'Datum', value: 'date' },
        { text: '', value: 'actions', sortable: false },
    ];
    userDialog = false;
    userFormState = AUTH_REQUEST_STATES.NOT_SENT;
    passwordReset = false;
    passwordResetState = AUTH_REQUEST_STATES.NOT_SENT;
    emailReset = false;
    emailResetState = AUTH_REQUEST_STATES.NOT_SENT;
    defaultFormData = {
        id: '',
        name: '',
        nickname: '',
        surname: '',
        image: null,
        bankAccount: '',
        notifications: [],
    };
    nameRules = [
        VALIDATION_RULES.REQUIRED,
    ];
    bankAccountRules = [
        VALIDATION_RULES.BANK_ACCOUNT,
    ];
    birthNumberRules = [
        value => !value || VALIDATION_RULES.BIRTH_NUMBER(value),
    ];
    icoRules = [
        value => !value || VALIDATION_RULES.ICO(value),
    ];
    formData = { ...this.defaultFormData };
    formImage = null;
    formImageRules = [
        VALIDATION_RULES.IMAGE,
    ];
    email = '';
    emailRules = [
        VALIDATION_RULES.REQUIRED,
        VALIDATION_RULES.EMAIL,
    ];
    password = '';
    passwordOk = true;
    passwordRules = [
        VALIDATION_RULES.REQUIRED,
    ];
    async logout() {
        await this.$store.dispatch('startLoading');
        await this.$store.dispatch('user/logout');
    }
    async openBackoffice() {
        // Open the tab synchronously in the click handler so popup blockers allow it.
        const tab = window.open('', '_blank');
        try {
            const issueBackofficeToken = functions.httpsCallable('issueBackofficeToken');
            const result = await issueBackofficeToken();
            const url = `${result.data.backofficeUrl}/sso?code=${result.data.code}`;
            if (tab)
                tab.location.href = url;
            else
                window.location.href = url;
        }
        catch (e) {
            console.error(e);
            tab?.close();
            Snackbar.error('Backoffice se nepodařilo otevřít!');
        }
    }
    get currentUserId() {
        return this.$store.getters['user/userId'];
    }
    get instructor() {
        return this.$store.getters['instructor/get'](this.currentUserId);
    }
    /**
     * Get the instructor's current grade with wage rates.
     * Returns null if instructor has no grade or lookup fails.
     */
    get currentGrade() {
        if (!this.instructor)
            return null;
        try {
            return getInstructorGradeForDate(this.instructor, moment());
        }
        catch {
            return null;
        }
    }
    /**
     * Check if wage information should be hidden.
     * Hide if user is not an instructor or has no grade assigned.
     */
    get hideWageSection() {
        return !this.instructor;
    }
    /**
     * Get the effective date of the instructor's current grade assignment.
     * Returns null for legacy records without gradeHistory.
     */
    get currentGradeEffectiveDate() {
        const gradeHistory = this.instructor?.data.gradeHistory;
        if (!gradeHistory || gradeHistory.length === 0)
            return null;
        // Sort by effective date descending to get the most recent assignment
        const sorted = [...gradeHistory].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
        return sorted[0].effectiveDate;
    }
    /**
     * Format effective date for display in Czech locale.
     * Example: "2024-12-01" -> "1. 12. 2024"
     */
    get formattedEffectiveDate() {
        if (!this.currentGradeEffectiveDate)
            return null;
        return moment(this.currentGradeEffectiveDate).format('D. M. YYYY');
    }
    get today() {
        return moment().format('YYYY-MM-DD');
    }
    /** Everything the instructor signed or still has to sign, pending first. */
    get papers() {
        return [...this.pendingPapers, ...this.signedPapers];
    }
    get signedPapers() {
        const contracts = this.$store.getters['contract/contracts']
            .filter((c) => c.data.instructorId === this.currentUserId)
            .map((c) => ({
            title: this.contractTemplate(c.data.templateId)?.data.title ?? c.data.key,
            key: c.data.key,
            status: CONTRACT_STATUS_TITLES[c.data.status] ?? c.data.status,
            date: this.sortableDate(c.data.instructorSignature?.signedAt),
            pending: false,
            kind: 'contract',
            templateId: c.data.templateId,
            record: c,
        }));
        const documents = this.$store.getters['document/documents']
            .filter((d) => d.data.instructorId === this.currentUserId)
            .map((d) => ({
            title: this.documentTemplate(d.data.templateId)?.data.title ?? d.data.key,
            key: d.data.key,
            status: DOCUMENT_STATUS_TITLES[d.data.status] ?? d.data.status,
            date: this.sortableDate(d.data.decidedAt),
            pending: false,
            kind: 'document',
            templateId: d.data.templateId,
            record: d,
        }));
        return [...contracts, ...documents];
    }
    get pendingPapers() {
        const keys = this.instructor?.data.templateKeys ?? [];
        const rows = [];
        for (const key of this.$store.getters['contract/pendingFor'](this.currentUserId, keys, this.today)) {
            rows.push(this.pendingPaper('contract', key, this.$store.getters['contract/templateFor'](key, this.today)));
        }
        for (const key of keys) {
            const template = this.$store.getters['document/templateFor'](key, this.today);
            if (!template)
                continue;
            if (this.$store.getters['document/decisionFor'](this.currentUserId, key, this.today))
                continue;
            rows.push(this.pendingPaper('document', key, template));
        }
        return rows;
    }
    pendingPaper(kind, key, template) {
        return {
            title: template.data.title,
            key,
            status: 'Nepodepsáno',
            date: PENDING_SORT_DATE,
            pending: true,
            kind,
            templateId: template.id,
        };
    }
    contractTemplate(id) {
        return this.$store.getters['contract/templates'].find((t) => t.id === id);
    }
    documentTemplate(id) {
        return this.$store.getters['document/templates'].find((t) => t.id === id);
    }
    sortableDate(value) {
        return value ? moment(value.toDate()).format('YYYY-MM-DD HH:mm') : '';
    }
    paperDateTitle(row) {
        if (row.pending || !row.date)
            return '—';
        return moment(row.date, 'YYYY-MM-DD HH:mm').format('D. M. YYYY HH:mm');
    }
    openSign(row) {
        this.documentsDialog = false;
        if (row.kind === 'contract') {
            ContractSignDialog.show(this.today, row.templateId);
            return;
        }
        DocumentSignDialog.show(this.today, row.templateId);
    }
    async downloadPaper(row) {
        try {
            const url = await this.$store.dispatch(`${row.kind}/pdfUrl`, row.record);
            window.open(url, '_blank');
        }
        catch (e) {
            console.error(e);
            Snackbar.error('PDF se nepodařilo stáhnout');
        }
    }
    get passwordInput() {
        return new VInput(this.$refs.passwordInput);
    }
    openUserDialog() {
        this.formData = {
            id: this.currentUserId,
            name: this.instructor.data.name,
            nickname: this.instructor.data.nickname ?? '',
            surname: this.instructor.data.surname,
            image: this.instructor.data.image,
            bankAccount: this.instructor.data.bankAccount ?? '',
            notifications: this.instructor.data.notifications,
            address: this.instructor.data.address ?? '',
            birthNumber: this.instructor.data.birthNumber ?? '',
            ico: this.instructor.data.ico ?? '',
        };
        this.userDialog = true;
    }
    async saveUserForm() {
        if (!this.$refs.userDialogForm.validate()) {
            return;
        }
        this.userFormState = AUTH_REQUEST_STATES.PENDING;
        try {
            await this.$store.dispatch('instructor/updateUser', this.formData);
            this.userFormState = AUTH_REQUEST_STATES.SENT;
            setTimeout(() => {
                this.userDialog = false;
                this.userFormState = AUTH_REQUEST_STATES.NOT_SENT;
            }, 500);
        }
        catch (e) {
            console.error(e);
            this.userFormState = AUTH_REQUEST_STATES.FAILED;
        }
    }
    async changePassword() {
        this.passwordResetState = AUTH_REQUEST_STATES.PENDING;
        try {
            await firebase.auth().sendPasswordResetEmail(this.instructor.data.email);
            this.passwordResetState = AUTH_REQUEST_STATES.SENT;
            setTimeout(() => {
                this.passwordReset = false;
                this.passwordResetState = AUTH_REQUEST_STATES.NOT_SENT;
            }, 500);
        }
        catch (e) {
            console.error(e);
            this.passwordResetState = AUTH_REQUEST_STATES.FAILED;
        }
    }
    async changeEmail() {
        if (!this.$refs.emailResetForm.validate()) {
            return;
        }
        this.emailResetState = AUTH_REQUEST_STATES.PENDING;
        try {
            const user = firebase.auth().currentUser;
            const credential = AuthCredential.fromJSON({
                email: this.instructor.data.email,
                password: this.password,
            });
            if (!user || !credential) {
                return;
            }
            try {
                await user.reauthenticateWithCredential(credential);
            }
            catch (e) {
                console.error(e);
                this.emailResetState = AUTH_REQUEST_STATES.NOT_SENT;
                this.passwordInput.pushCustomError('Nesprávné heslo');
                return;
            }
            await user.updateEmail(this.email);
            const instructor = { ...this.instructor };
            instructor.data.email = this.email;
            await this.$store.dispatch('instructor/update', instructor);
            this.emailResetState = AUTH_REQUEST_STATES.SENT;
            setTimeout(() => {
                this.emailReset = false;
                this.emailResetState = AUTH_REQUEST_STATES.NOT_SENT;
                this.logout();
            }, 500);
        }
        catch (e) {
            console.error(e);
            this.emailResetState = AUTH_REQUEST_STATES.FAILED;
        }
    }
    async mounted() {
        this.instructor;
        await this.$nextTick();
        this.listeners.push(...(await this.$store.dispatch('transaction/listenSpecific', this.currentUserId)));
    }
    beforeDestroy() {
        for (let endListener of this.listeners) {
            endListener();
        }
    }
    async onInputFileChange(file) {
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
            if (typeof reader.result !== 'string') {
                return;
            }
            // create offscreen canvas
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            // create image
            const image = new Image();
            image.src = reader.result;
            // await image load
            await new Promise(resolve => image.onload = () => resolve());
            // calculate image dimensions
            const size = 128;
            const ratio = image.width / image.height;
            const width = image.width > image.height ? size * ratio : size;
            const height = image.height > image.width ? size / ratio : size;
            // calculate image offset
            const dx = width > height ? -(width - height) / 2 : 0;
            const dy = height > width ? -(height - width) / 2 : 0;
            // render cropped image to offscreen canvas
            canvas.width = canvas.height = size;
            ctx.drawImage(image, dx, dy, width, height);
            // set base64 image to formData object
            this.formData.image = canvas.toDataURL();
        };
        reader.onerror = error => console.error(error);
    }
    onInputChanged() {
        this.passwordInput.clearCustomError("Nesprávné heslo");
    }
    onEmailResetDialogClose(value) {
        if (value) {
            return;
        }
        this.email = '';
        this.password = '';
        this.$refs.emailResetForm.reset();
    }
    onUserDialogClose(value) {
        if (value) {
            return;
        }
        this.formData = { ...this.defaultFormData };
        this.$refs.userDialogForm.reset();
        this.$refs.fileInput.reset();
    }
};
__decorate([
    Watch('formImage')
], UserMenu.prototype, "onInputFileChange", null);
__decorate([
    Watch("password")
], UserMenu.prototype, "onInputChanged", null);
__decorate([
    Watch("emailReset")
], UserMenu.prototype, "onEmailResetDialogClose", null);
__decorate([
    Watch("userDialog")
], UserMenu.prototype, "onUserDialogClose", null);
UserMenu = __decorate([
    Component({
        components: { UserStatistics }
    })
], UserMenu);
export default UserMenu;
